import React, { useState, useEffect } from 'react';
import { hostingService, HostingPackage } from '../../services/hostingService';
import { useAuth } from '../../contexts/AuthContext';

const BusinessEmailPackages: React.FC = () => {
  const [packages, setPackages] = useState<HostingPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const result = await hostingService.getEmailPackages();
      if (result.success) {
        setPackages(result.data);
      } else {
        setError(result.message || 'Failed to fetch email packages');
      }
    } catch (error) {
      setError('Failed to fetch email packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (packageId: string, packageName: string) => {
    // Redirect directly to the Email checkout onboarding process
    window.location.href = `/checkout/email/${packageId}`;
  };

  if (loading) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading email packages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-700">{error}</p>
              <button 
                onClick={fetchPackages}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-16 shadow-xl text-center relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <svg className="w-12 h-12 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">No email packages found</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">Business email plans will be available soon. Check back later for professional email hosting options.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
            <span className="text-sm font-semibold text-gray-700">Business Email</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Email</span> Plan
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Professional business email hosting with custom domains and advanced features. 
            Build trust with clients using your own domain email addresses.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          {packages.map((pkg, index) => (
            <div key={pkg.id} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group">
              {/* Glossy overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              <div className="relative z-10">
                {/* Package Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 rounded-2xl mb-4 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                    <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">{pkg.description}</p>
                  <div className="flex justify-center items-baseline mb-6">
                    <span className="text-4xl font-black text-primary-navy">${pkg.price_monthly || pkg.price}</span>
                    <span className="text-gray-600 text-lg ml-2">/month</span>
                  </div>
                </div>

                {/* Package Features */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">What's Included:</h4>
                  <ul className="space-y-3">
                    {Array.isArray(pkg.features) && pkg.features.length > 0 ? (
                      pkg.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500 text-sm text-center">No features available</li>
                    )}
                  </ul>
                </div>

                {/* Additional Benefits */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 border border-orange-200">
                    <h5 className="text-sm font-semibold text-orange-700 mb-2 text-center">Included Benefits:</h5>
                    <div className="space-y-1 text-xs text-orange-600 text-center">
                      <p>✓ Hourly off-site backups</p>
                      <p>✓ Spam filtering + SPF, DKIM & DMARC</p>
                      <p>✓ Webmail + any mail app</p>
                      <p>✓ Extra mailboxes any time</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleSelectPackage(pkg.id, pkg.name)}
                  className="w-full bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
                  <span className="relative z-10">Get Business Email</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-20">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-12 shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full">
                  <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
                  <span className="text-sm font-semibold text-gray-700">Why Choose Us</span>
                </div>
                
                <h3 className="text-4xl font-black text-gray-900 mb-4">
                  Why Choose <span className="text-primary-navy">12and22</span> Business Email?
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                    title: 'Secure by Default',
                    description: 'TLS on every connection, spam filtering, and SPF, DKIM and DMARC configured for your domain',
                    color: 'from-orange-500 via-yellow-500 to-orange-600'
                  },
                  {
                    icon: <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m-9 9a9 9 0 019-9" /></svg>,
                    title: 'Professional Image',
                    description: 'Custom domain emails that enhance your business credibility',
                    color: 'from-primary-navy via-primary-800 to-primary-900'
                  },
                  {
                    icon: <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
                    title: 'Any Device, Any App',
                    description: 'Outlook, Apple Mail, Thunderbird, Gmail app and more, with one-click setup profiles',
                    color: 'from-orange-500 via-yellow-500 to-orange-600'
                  }
                ].map((feature, index) => (
                  <div key={index} className="text-center">
                    <div className={`w-16 h-16 ${index === 0 || index === 2 ? 'bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600' : 'bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900'} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg relative overflow-hidden`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                      <div className="relative z-10">{feature.icon}</div>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessEmailPackages;